import React, { useEffect, useState } from 'react'
import { Property } from '../../../../types/Property/Property'
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
  formatPrice,
} from '../../../../lib/utils/formatNumber'
import { HeroIcons } from '../../../../lib/utils/heroIcons'
import LinkButton, { buttonHierarchy, buttonShape } from '../../../Button'
import FractionInput from './FractionInput'
import { useTranslation } from 'next-i18next'
import routes from '../../../../config/routes'
import { BillingAddress } from '../../../../types/User/User'
import useCurrency from '../../../../hooks/useCurrency'
import useLocale from '../../../../hooks/useLocale'
import { OngoingOrder } from '../../../../types/Order/OngoingOrder'
import OrderService from '../../../../services/order.service'
import { getLocalePathFromPathname } from '../../../../lib/utils/language'
import { useAuth } from '../../../../providers/AuthProvider'
import { GoogleTagManagerService } from '../../../../services/google-tag-manager.service'
import { CurrencyEnum } from '../../../../types/Currency'
import { mapContent, mapItem } from '../../../../lib/utils/helperGtm'
import { ClubEntry, ClubEntryStatus } from '../../../../types/club/ClubEntry'
import { formatDateString } from '../../../../lib/utils/formatDate'

type BuyCardProps = {
  property: Property
  minFractionAmount: number
  clubEntry?: ClubEntry
}

export default function BuyCard({
  property,
  minFractionAmount,
  clubEntry,
}: BuyCardProps) {
  const { t } = useTranslation('originals/property')
  const { currency } = useCurrency()
  const { locale } = useLocale()
  const { isLoggedIn } = useAuth()

  const [shareLinkCopied, setShareLinkCopied] = useState<boolean>(false)
  const [ongoingOrder, setOngoingOrder] = useState<OngoingOrder>()
  const [fractionAmount, setFractionAmount] =
    useState<number>(minFractionAmount)
  useState<BillingAddress>()

  const isFundingClosed = ['funded', 'reimbursed'].includes(
    property.fundingStatus,
  )
  const isReimbursed = property.fundingStatus === 'reimbursed'

  const isDisabled =
    property.fundingStatus !== 'funding' || fractionAmount === 0

  const handleValueChange = (value: number): void => {
    setFractionAmount(value)
  }

  const fetchOrder = async (propertyId: string) => {
    try {
      const ongoingOrder: OngoingOrder =
        await OrderService.getOngoingOrder(propertyId)
      setOngoingOrder(ongoingOrder)
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    if (isLoggedIn) {
      fetchOrder(property.id)
    } else {
      setOngoingOrder(undefined)
    }
  }, [locale, isLoggedIn])

  useEffect(() => {
    setFractionAmount(minFractionAmount)
  }, [minFractionAmount])

  const onShareLink = (): void => {
    navigator.clipboard.writeText(window.location.href)
    setShareLinkCopied(true)
    setTimeout(() => setShareLinkCopied(false), 2000)
  }

  const getFundingDateText = (): string | null => {
    if (property.fundingStatus === 'funded') {
      return t('buyCard.funded')
    }
    if (property.fundingStatus === 'reimbursed') {
      return t('buyCard.reimbursed')
    }

    if (!property.fundingEndAt && !property.fundingStartAt) {
      return null
    }

    if (property.fundingStatus === 'not_started' && property.fundingStartAt) {
      const startDate = new Date(property.fundingStartAt)
      return t('buyCard.opensOn', {
        date: formatDateString(startDate, locale, 'D MMM à HH[h]mm'),
      })
    }

    if (property.fundingStatus === 'funding' && property.fundingEndAt) {
      const endDate = new Date(property.fundingEndAt)
      return t('buyCard.endsOn', {
        date: formatDateString(endDate, locale, 'D MMM à HH[h]mm'),
      })
    }

    return null
  }

  const fundingDateText = getFundingDateText()
  const fundingBadgeClass = isReimbursed
    ? 'bg-gradient-to-r from-emerald-100 via-teal-100 to-cyan-100 text-emerald-900 ring-emerald-200'
    : isFundingClosed
      ? 'bg-gradient-to-r from-amber-200 via-rose-200 to-emerald-200 text-emerald-900 ring-emerald-200'
      : 'bg-amber-200 text-amber-900 ring-amber-300'

  return (
    <div className="rounded-2xl bg-neutralWhite p-2.5">
      {fundingDateText && (
        <div className="mb-1 text-left">
          <span
            className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-semibold shadow-sm ring-1 ${fundingBadgeClass}`}
          >
            {fundingDateText}
          </span>
        </div>
      )}

      <div className="border-b-2 border-neutralGray/50 pb-3">
        <div className="flex items-center">
          <div>
            <div>
              <div className="text-md font-thin text-neutralBlack">
                {t('buyCard.amountToCollect')}
              </div>
              <div className="text-3xl font-medium text-neutralBlack">
                {formatCurrency(
                  property.fractionCount * property.fractionInitialPrice,
                  0,
                  2,
                  currency,
                  locale,
                )}
              </div>
            </div>
            <div className="text-sm text-neutralBlack">
              {formatNumber(property.fractionCount)} {t('buyCard.fractionsAt')}{' '}
              <strong>
                {formatCurrency(
                  property.fractionInitialPrice,
                  0,
                  2,
                  currency,
                  locale,
                )}
              </strong>
            </div>
          </div>

          <div className="ml-auto flex items-center">
            <button onClick={onShareLink}>
              <div className="flex items-center font-bold text-neutralBlack hover:text-brandLightBlue">
                {shareLinkCopied ? (
                  <HeroIcons.CheckIcon
                    className="mr-1 h-5 w-5"
                    aria-hidden="true"
                  />
                ) : (
                  <HeroIcons.ShareIcon
                    className="mr-1 h-5 w-5"
                    aria-hidden="true"
                  />
                )}
                {shareLinkCopied
                  ? t('topBar.shareLinkCopied')
                  : t('topBar.share')}
              </div>
            </button>
          </div>
        </div>
      </div>
      <div className="border-b-2 border-neutralGray/50 pb-3 pt-3">
        {property.revaluationPerYear > 0 ? (
          <div className="flex">
            <div className="flex items-center">
              <HeroIcons.ChartBarIcon
                className="mr-2 h-5 w-5 text-neutralBlack"
                aria-hidden="true"
              />
              <div className="text-neutralBlack">
                {t('buyCard.propertyReassessment')}
              </div>
            </div>
            <div className="flex flex-auto items-center justify-center px-2">
              <div className="flex h-[1px] w-full border-t-[0.5px] border-dashed border-neutralGray/50 text-center"></div>
            </div>
            <div className="flex text-right">
              <div className="ml-1 font-bold text-neutralBlack">
                {formatPercentage(property.revaluationPerYear, 2, true)}
              </div>
            </div>
          </div>
        ) : null}
        <div className="flex">
          <div className="flex items-center">
            <HeroIcons.BanknotesIcon
              className="mr-2 h-5 w-5 text-neutralBlack"
              aria-hidden="true"
            />
            <div className="text-neutralBlack">
              {t('buyCard.netRentalYields')}
            </div>
          </div>
          <div className="flex flex-auto items-center justify-center px-2">
            <div className="flex h-[1px] w-full border-t-[0.5px] border-dashed border-neutralGray/50 text-center"></div>
          </div>
          <div className="flex text-right">
            <div className="ml-1 font-bold text-neutralBlack">
              {formatPercentage(property.rentalYieldPerYear, 2, true)}
            </div>
          </div>
        </div>
        {property.timeFrameMonths ? (
          <div className="flex">
            <div className="flex items-center">
              <HeroIcons.ClockIcon
                className="mr-2 h-5 w-5 text-neutralBlack"
                aria-hidden="true"
              />
              <div className="text-neutralBlack">
                {t('buyCard.propertyDuration')}
              </div>
            </div>
            <div className="flex flex-auto items-center justify-center px-2">
              <div className="flex h-[1px] w-full border-t-[0.5px] border-dashed border-neutralGray/50 text-center"></div>
            </div>
            <div className="flex text-right">
              <div className="ml-1 font-bold text-neutralBlack">
                {t('buyCard.propertyDurationInMonth', {
                  months: property.timeFrameMonths,
                })}
              </div>
            </div>
          </div>
        ) : null}
      </div>
      <div className="border-neutralGray/50 pt-3">
        {ongoingOrder ? (
          <div className="mb-1">
            <p className="font-light text-neutralBlack">
              {t('buyCard.pendingPurchase')}
            </p>
          </div>
        ) : (
          <div className="mb-3">
            <FractionInput
              property={property}
              onValueChange={handleValueChange}
              minFractionAmount={minFractionAmount}
              initialValue={minFractionAmount}
            />
            {clubEntry && clubEntry.status !== ClubEntryStatus.COMPLETED && (
              <div className="ml-0.5 mt-2">
                <p className="text-xs text-gray-600">
                  {t('buyCard.clubdeal.minimumFraction')}
                </p>
                <p className="mt-1 text-xs italic text-gray-600">
                  {t('buyCard.clubdeal.contactUs')}
                </p>
              </div>
            )}
          </div>
        )}

        <LinkButton
          hierarchy={buttonHierarchy.primary}
          shape={buttonShape.rectangle}
          className="w-full py-1 text-lg font-bold"
          link={
            ongoingOrder &&
            ongoingOrder.stockLock &&
            ongoingOrder.stockLock.lock
              ? {
                  pathname: getLocalePathFromPathname(routes.checkout, locale),
                  query: { propertyId: property.id },
                }
              : {
                  pathname: getLocalePathFromPathname(
                    routes.checkoutQueue,
                    locale,
                  ),
                  query: {
                    propertyId: property.id,
                    fractionAmount: ongoingOrder
                      ? undefined
                      : fractionAmount.toString(),
                  },
                }
          }
          onClick={(e) => {
            if (isDisabled) {
              e.preventDefault()
              e.stopPropagation()
              return
            }

            GoogleTagManagerService.AddToCart({
              value: formatPrice(
                property.fractionCurrentPrice * fractionAmount,
              ),
              currency: CurrencyEnum.EUR,
              contents: [mapContent(property, fractionAmount)],
              items: [mapItem(property, fractionAmount)],
            })
          }}
          disabled={isDisabled}
        >
          {ongoingOrder ? t('buyCard.continuePurchase') : t('buyCard.proceed')}
        </LinkButton>
      </div>
    </div>
  )
}
